/**
 * PhilPage.js
 * Philosophy Explorer - Main Page Component
 *
 * LOCATION: ckg3-frontend/src/components/Phil/PhilPage.js
 *
 * Conversation-first interface for Phil. Phil is a philosophical companion
 * that helps users examine their thinking as captured in their knowledge base.
 *
 * Key principles:
 * - "Thinking as Object" — Phil examines thinking, not persons
 * - Contemplative pace, not transactional
 * - User sets the agenda; Phil offers, never directs
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PhilConsentModal from './PhilConsentModal';
import PhilOrientationModal from './PhilOrientationModal';
import './PhilPage.css';

// =========================================
// Constants
// =========================================

const PHIL_STATES = {
  INITIALIZING: 'initializing',
  READY: 'ready',
  IN_SESSION: 'in_session',
  ERROR: 'error'
};

// Topics sidebar feature flag
// Set to false to hide the "Topics Ready for Exploration" sidebar.
// This sidebar is intentionally hidden during the Phil 2.0 transition.
// It may be revived in a future redesign once synthesis-based conversations are mature.
const SHOW_TOPICS_SIDEBAR = false;

// Phil's opening message when session starts
const PHIL_OPENING = "Would you like me to start this conversation, or do you have something you'd like to think through?";

// Phil's closing message when user ends session
const PHIL_CLOSING = "Alright. This will be here if you want to return to it.";

// =========================================
// Document reference tokens
// =========================================
// Phil names the user's own sources with a reference token in message text:
//   [The Expert Mind](doc:5f0e-…)
// The id is an opaque docReferenceId; the backend never sends URLs to the model.
// We resolve ids → the user's own document urls and render the title as a link.
//
// This is deliberately NOT a markdown pipeline. Running Phil's messages through
// ReactMarkdown (as AI Explorer does for its synthesis output) would newly
// interpret hashes, hyphens, backticks and blockquote markers in his writing.
// This renderer does exactly two things: it resolves the reference token, and it
// strips paired emphasis markers (see EMPHASIS_RE below). Nothing else in the
// text is touched — the same narrow contract AI Explorer's [N](#source-N)
// citation override holds, at a smaller scope.
//
// AN EARLIER VERSION OF THIS COMMENT CLAIMED "Phil writes prose, not markdown".
// That was an assumption, not an instruction: nothing in PhilPrompt.js or the
// character brief ever told Phil to avoid markdown, so he emphasised the way any
// model writing conversational prose does and the markers reached the reader as
// literal asterisks ("what the work is *for*"). Ruled 2026-10-09 (D8): strip the
// markers at display time rather than ask the model to change how it writes.
// Emphasis is removed, not rendered — that keeps this renderer's promise not to
// interpret Phil's punctuation, and whether Phil should HAVE emphasis is a
// separate question about his voice.
//
// The id group accepts an empty match so a malformed token like `[Title](doc:)`
// is still consumed here and degrades to plain text, rather than escaping the
// parser and displaying raw.
const DOC_TOKEN_RE = /\[([^\][]*)\]\(doc:([^)]*)\)/g;

// Orphan fragments — a `(doc:…)` with no preceding bracket, left by a model slip
// or a truncated message. Stripped so raw token syntax never reaches the reader.
// (Any space immediately before the fragment goes with it, so stripping doesn't
// leave a doubled space in the sentence.)
const DOC_ORPHAN_RE = /[ \t]?\(doc:[^)]*\)/g;

// Paired emphasis markers, removed at display time (ruled 2026-10-09, D8).
// `**bold**` and `*italic*` become their text; the marker characters go.
//
// THE GUARDS MATTER MORE THAN THE MATCH. Each side requires a NON-SPACE
// character immediately inside the marker, and the body may not contain the
// marker character, so:
//   - `2 * 3 * 4` is left alone (spaces inside the markers)
//   - a lone `*` is left alone (nothing to pair with)
//   - `5*6` is left alone (no closing pair around a non-space body... and if it
//     did pair, the result would still read correctly)
// Longer markers are tried first (`***`, then `**`, then `*`) so a bold span is
// not mis-read as two italic ones. Underscores are NOT matched: they occur
// inside identifiers and file names far more often than they mean emphasis in
// Phil's register.
//
// The body may not contain `*`, so a nested pair ("**bold *italic* bold**") is
// unwrapped from the inside out — which is why stripEmphasis below runs the
// replacement until it stops changing, bounded so it can never spin.
const EMPHASIS_RE = /(\*\*\*|\*\*|\*)(?=\S)([^*]*[^\s*]|\S)\1/g;

/** Remove paired emphasis markers, innermost first. Bounded; never throws. */
const stripEmphasis = (s) => {
  let out = s;
  for (let pass = 0; pass < 3; pass += 1) {
    const next = out.replace(EMPHASIS_RE, '$2');
    if (next === out) break;
    out = next;
  }
  return out;
};

/** Harvest every docReferenceId referenced in a block of Phil text. */
const extractDocIds = (text) => {
  const ids = [];
  if (typeof text !== 'string') return ids;
  for (const match of text.matchAll(DOC_TOKEN_RE)) {
    const id = (match[2] || '').trim();
    if (id) ids.push(id);
  }
  return ids;
};

/**
 * Render Phil's message text, turning resolved reference tokens into links.
 *
 * Fallbacks (both required, both silent):
 *  - unknown id, unowned id, or a document with no url → the title renders as
 *    plain text; no dead link, no error.
 *  - malformed or orphaned token syntax → stripped to its text.
 *
 * @param {string} text - Phil's message content
 * @param {Object} docMap - id → { title, url }, from /api/phil/documents/resolve
 * @returns {Array} React children
 */
const renderPhilContent = (text, docMap) => {
  if (typeof text !== 'string' || !text) return text;

  // Strip paired emphasis markers BEFORE tokenizing, so the doc-token matcher
  // still sees intact `[Title](doc:id)` syntax — the pattern touches only `*`,
  // never brackets or parentheses, so a reference token cannot be damaged by it.
  text = stripEmphasis(text);

  const clean = (s) => s.replace(DOC_ORPHAN_RE, '');
  const parts = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(DOC_TOKEN_RE)) {
    const [full, label, rawId] = match;
    const start = match.index;
    if (start > lastIndex) parts.push(clean(text.slice(lastIndex, start)));
    lastIndex = start + full.length;

    const id = (rawId || '').trim();
    const title = (label || '').trim();
    const doc = id ? docMap?.[id] : null;

    if (doc?.url && title) {
      parts.push(
        <a
          key={`doc-${key++}`}
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          className="phil-doc-link"
        >
          {title}
        </a>
      );
    } else {
      // Unresolved: the words stay, the link doesn't appear.
      parts.push(title);
    }
  }

  if (lastIndex < text.length) parts.push(clean(text.slice(lastIndex)));
  return parts.length > 0 ? parts : clean(text);
};

// =========================================
// Main Component
// =========================================

const PhilPage = () => {
  // =========================================
  // Context & Auth
  // =========================================
  const { user, refreshUser, isAdmin } = useAuth();
  const location = useLocation();

  // =========================================
  // State Management
  // =========================================

  // Orientation state
  const [showOrientationModal, setShowOrientationModal] = useState(false);
  const [orientationReadOnly, setOrientationReadOnly] = useState(false);
  const [orientationCompleted, setOrientationCompleted] = useState(false);

  // Cluster/initialization state
  const [philState, setPhilState] = useState(PHIL_STATES.INITIALIZING);
  const [readyClusters, setReadyClusters] = useState([]);
  const [totalReadyClusters, setTotalReadyClusters] = useState(0);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [awaitingOpeningChoice, setAwaitingOpeningChoice] = useState(false);

  // Consent modal state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);

  // Session ID copy state (admin only)
  const [sessionIdCopied, setSessionIdCopied] = useState(false);

  // Resolved document references (id → { title, url }) for Phil's [text](doc:id)
  // tokens. Accumulates across the session; ids already resolved are never re-fetched.
  const [docMap, setDocMap] = useState({});

  // Refs
  const conversationRef = useRef(null);
  const inputRef = useRef(null);

  // =========================================
  // Data Loading
  // =========================================

  const loadReadyClusters = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.phil.getReadyClusters({ limit: 20 });

      setReadyClusters(response.clusters || []);
      setTotalReadyClusters(response.totalReady || 0);
      setPhilState(PHIL_STATES.READY);

    } catch (err) {
      console.error('Failed to load Phil-ready clusters:', err);
      setError('Unable to load your thinking clusters. Please try again.');
      setPhilState(PHIL_STATES.ERROR);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =========================================
  // Orientation Management
  // =========================================

  // Check orientation status on mount
  useEffect(() => {
    if (user) {
      const hasCompletedOrientation = !!user.philOrientationCompletedAt;
      setOrientationCompleted(hasCompletedOrientation);

      // Show modal if orientation not completed
      if (!hasCompletedOrientation) {
        setShowOrientationModal(true);
        setOrientationReadOnly(false);
      }
    }
  }, [user]);

  // Check for showOrientation query parameter (from sidebar link)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('showOrientation') === 'true' && user?.philOrientationCompletedAt) {
      // User clicked the orientation date link in sidebar - show in read-only mode
      setShowOrientationModal(true);
      setOrientationReadOnly(true);
    }
  }, [location.search, user]);

  const handleCompleteOrientation = useCallback(async () => {
    try {
      await apiService.phil.completeOrientation();

      // Refresh user data to get updated philOrientationCompletedAt
      if (refreshUser) {
        await refreshUser();
      }

      setOrientationCompleted(true);
      setShowOrientationModal(false);
      setOrientationReadOnly(false);
    } catch (err) {
      console.error('Failed to complete orientation:', err);
      // Still close modal and allow access - don't block user
      setShowOrientationModal(false);
    }
  }, [refreshUser]);

  const handleReopenOrientation = useCallback(() => {
    setShowOrientationModal(true);
    setOrientationReadOnly(true);
  }, []);

  const handleCloseOrientationReadOnly = useCallback(() => {
    setShowOrientationModal(false);
    setOrientationReadOnly(false);
  }, []);

  // =========================================
  // Effects
  // =========================================

  useEffect(() => {
    loadReadyClusters();
  }, [loadReadyClusters]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when session becomes active
  useEffect(() => {
    if (sessionActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [sessionActive]);

  // Resolve any new document references Phil has named. Until a reference resolves
  // (or if it never does) its title renders as plain text, so this never gates the
  // message from appearing.
  useEffect(() => {
    const unresolved = [...new Set(
      messages
        .filter(m => m.role === 'phil')
        .flatMap(m => extractDocIds(m.content))
        .filter(id => !(id in docMap))
    )];
    if (unresolved.length === 0) return;

    let cancelled = false;
    (async () => {
      const resolved = await apiService.phil.resolveDocuments(unresolved);
      if (cancelled) return;
      setDocMap(prev => {
        const next = { ...prev };
        // Record every id we asked about — including the ones that came back
        // empty — so an unowned or url-less reference is asked about only once.
        for (const id of unresolved) next[id] = resolved[id] || null;
        return next;
      });
    })();

    return () => { cancelled = true; };
  }, [messages, docMap]);

  // Browser close/refresh handler
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (sessionActive && sessionId) {
        // Save with unconfirmed flag via keepalive fetch (sendBeacon cannot set custom headers)
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        fetch(`${apiUrl}/api/phil/session/${sessionId}/end`, {
          method: 'POST',
          keepalive: true,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ persist: true, unconfirmed: true })
        }).catch(() => { /* best-effort on page unload */ });

        // Show browser's generic warning
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sessionActive, sessionId]);

  // =========================================
  // Message Helpers
  // =========================================

  const addMessage = useCallback((role, content) => {
    setMessages(prev => [...prev, {
      id: `${role}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role,
      content,
      timestamp: new Date()
    }]);
  }, []);

  // =========================================
  // Session Event Handlers
  // =========================================

  const handleStartSession = useCallback(async (cluster = null) => {
    try {
      setIsThinking(true);
      setError(null);

      // Phil 2.0: Use synthesis-based initialization
      const response = await apiService.phil.startSession();

      // Handle case where user needs to explore first
      if (response.needsExploration) {
        setMessages([{
          id: `phil-${Date.now()}`,
          role: 'phil',
          content: response.message,
          timestamp: new Date()
        }]);
        setSessionActive(false);
        setIsThinking(false);
        return;
      }

      // Phil has an opening based on user's syntheses
      if (response.opening && response.sessionId) {
        setSessionId(response.sessionId); // Store the session ID
        setSessionActive(true);
        setPhilState(PHIL_STATES.IN_SESSION);

        setMessages([{
          id: `phil-${Date.now()}`,
          role: 'phil',
          content: response.opening,
          timestamp: new Date(),
          metadata: response.metadata // Include synthesis metadata
        }]);

        // User can now respond directly - no opening choice needed
        setAwaitingOpeningChoice(false);
      }

    } catch (err) {
      console.error('Failed to start Phil session:', err);
      setError('Unable to start conversation. Please try again.');
    } finally {
      setIsThinking(false);
    }
  }, []);

  // User chooses "You start" - Phil leads
  const handlePhilStarts = useCallback(async () => {
    setAwaitingOpeningChoice(false);
    setIsThinking(true);

    try {
      // Send special marker so backend knows Phil should lead
      const response = await apiService.phil.sendMessage(sessionId, '__phil_start__');

      addMessage('phil', response.response || response.question || "What's been on your mind lately?");
    } catch (err) {
      console.error('Failed to get Phil opening:', err);
      addMessage('phil', "I'm having trouble getting started. What's on your mind?");
    } finally {
      setIsThinking(false);
    }
  }, [sessionId, addMessage]);

  // User sends their own message
  const handleSend = useCallback(async () => {
    const message = inputValue.trim();
    if (!message || isThinking) return;

    setAwaitingOpeningChoice(false);
    setInputValue('');
    addMessage('user', message);
    setIsThinking(true);

    try {
      const response = await apiService.phil.sendMessage(sessionId, message);

      // Handle crisis detection
      if (response.crisis) {
        addMessage('phil', response.response);
      }
      // Handle full conversation recall - display all previous turns
      else if ((response.responseType === 'recall_loaded' || response.responseType === 'recall_loaded_full') && response.conversationHistory) {
        // Build complete message list from recalled conversation
        const recalledMessages = [];

        response.conversationHistory.forEach((turn, index) => {
          // Map roles based on initiator field
          // initiator: 'phil' means Phil asked (prompt), user answered (response)
          // initiator: 'user' means user asked (prompt), Phil answered (response)

          if (turn.prompt) {
            recalledMessages.push({
              id: `recalled-${index}-prompt`,
              role: turn.initiator === 'phil' ? 'phil' : 'user',  // Use initiator to determine role
              content: turn.prompt,
              timestamp: turn.createdAt ? new Date(turn.createdAt) : new Date()
            });
          }
          if (turn.response) {
            recalledMessages.push({
              id: `recalled-${index}-response`,
              role: turn.initiator === 'phil' ? 'user' : 'phil',  // Response is opposite of initiator
              content: turn.response,
              timestamp: turn.createdAt ? new Date(turn.createdAt) : new Date()
            });
          }
        });

        // Replace all messages with recalled conversation
        setMessages(recalledMessages);
      }
      else {
        addMessage('phil', response.response || response.question || "Tell me more about that.");
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      addMessage('phil', "I'm having trouble responding. Could you try again?");
    } finally {
      setIsThinking(false);
    }
  }, [inputValue, isThinking, sessionId, addMessage]);

  // End the conversation - shows consent modal
  const handleEndConversation = useCallback(() => {
    setShowConsentModal(true);
  }, []);

  // Reset session state after ending
  const resetSession = useCallback(() => {
    setSessionId(null);
    setSessionActive(false);
    setMessages([]);
    setAwaitingOpeningChoice(false);
    setPhilState(PHIL_STATES.READY);
    setSelectedCluster(null);
    setShowConsentModal(false);
    setConsentLoading(false);
  }, []);

  // User chooses to keep the conversation
  const handleConsentKeep = useCallback(async () => {
    setConsentLoading(true);

    try {
      await apiService.phil.endSession(sessionId, { persist: true });
    } catch (err) {
      console.error('Failed to save session:', err);
      // Continue anyway - session cleanup still needed
    }

    // Add closing message and reset
    addMessage('phil', PHIL_CLOSING);

    setTimeout(() => {
      resetSession();
    }, 1500);
  }, [sessionId, addMessage, resetSession]);

  // User chooses to discard the conversation
  const handleConsentDiscard = useCallback(async () => {
    // Don't set loading state for discard - it's instant and shouldn't disable the button
    try {
      await apiService.phil.endSession(sessionId, { persist: false });
    } catch (err) {
      console.error('Failed to end session:', err);
      // Continue anyway
    }

    // Add closing message and reset
    addMessage('phil', "Alright. This conversation won't be saved.");

    setTimeout(() => {
      resetSession();
    }, 1500);
  }, [sessionId, addMessage, resetSession]);

  // Handle keyboard input
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Copy session ID to clipboard (admin only)
  const handleCopySessionId = useCallback(async () => {
    if (!sessionId) return;

    try {
      await navigator.clipboard.writeText(sessionId);
      setSessionIdCopied(true);
      setTimeout(() => setSessionIdCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy session ID:', err);
    }
  }, [sessionId]);

  // =========================================
  // Cluster Selection
  // =========================================

  const handleClusterSelect = useCallback((cluster) => {
    setSelectedCluster(cluster);
    handleStartSession(cluster);
  }, [handleStartSession]);

  const handleStartWithoutTopic = useCallback(() => {
    setSelectedCluster(null);
    handleStartSession(null);
  }, [handleStartSession]);

  const handleRetry = useCallback(() => {
    loadReadyClusters();
  }, [loadReadyClusters]);

  // =========================================
  // Render Functions
  // =========================================

  const renderParticleField = (subtle = false) => (
    <div className={`phil-particle-field ${subtle ? 'phil-particle-field-subtle' : ''}`}>
      <div className="phil-particle" style={{ '--delay': '0s', '--duration': '4s' }} />
      <div className="phil-particle" style={{ '--delay': '0.5s', '--duration': '5s' }} />
      <div className="phil-particle" style={{ '--delay': '1s', '--duration': '4.5s' }} />
      <div className="phil-particle" style={{ '--delay': '1.5s', '--duration': '3.5s' }} />
      <div className="phil-particle" style={{ '--delay': '2s', '--duration': '5.5s' }} />
      <div className="phil-particle" style={{ '--delay': '2.5s', '--duration': '4s' }} />
      <div className="phil-particle" style={{ '--delay': '3s', '--duration': '6s' }} />
      <div className="phil-particle" style={{ '--delay': '0.3s', '--duration': '4.2s' }} />
    </div>
  );

  const renderConversation = () => (
    <div className="phil-conversation-container">
      {/* Ambient background - subtle particles */}
      <div className="phil-conversation-background">
        {renderParticleField(true)}
      </div>

      {/* Conversation history - scrolls */}
      <div className="phil-conversation-history" ref={conversationRef}>
        {messages.map(msg => (
          <div key={msg.id} className={`phil-message phil-message-${msg.role}`}>
            <span className="phil-message-role">
              {msg.role === 'phil' ? 'Phil' : 'You'}
            </span>
            <p className="phil-message-content">
              {msg.role === 'phil' ? renderPhilContent(msg.content, docMap) : msg.content}
            </p>
          </div>
        ))}

        {isThinking && (
          <div className="phil-message phil-message-phil phil-thinking">
            <span className="phil-message-role">Phil</span>
            <p className="phil-thinking-indicator">...</p>
          </div>
        )}
      </div>

      {/* Opening choice buttons - only shown initially */}
      {awaitingOpeningChoice && (
        <div className="phil-opening-choice">
          <button onClick={handlePhilStarts} disabled={isThinking}>
            You start
          </button>
        </div>
      )}

      {/* Input area */}
      <div className="phil-input-container">
        <input
          ref={inputRef}
          type="text"
          className="phil-input"
          placeholder="Type your response..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isThinking}
        />
        <button
          className="phil-send-button"
          onClick={handleSend}
          disabled={!inputValue.trim() || isThinking}
        >
          Send
        </button>
        <button
          className="phil-end-button"
          onClick={handleEndConversation}
          disabled={isThinking}
        >
          Take a breath
        </button>
      </div>

      {/* Admin-only session ID display */}
      {isAdmin && sessionId && (
        <div
          className="phil-session-id-display"
          onClick={handleCopySessionId}
          title="Click to copy session ID"
        >
          <span className="phil-session-id-label">Session:</span>
          <span className="phil-session-id-value">{sessionId}</span>
          {sessionIdCopied && <span className="phil-session-id-copied">Copied!</span>}
        </div>
      )}
    </div>
  );

  const renderVisualizationPlaceholder = () => (
    <div className="phil-visualization-container">
      <div className="phil-visualization-placeholder">
        {renderParticleField()}

        <div className="phil-placeholder-content">
          <h2 className="phil-placeholder-title">Phil</h2>
          <p className="phil-placeholder-subtitle">Philosophy Explorer</p>

          {/* Start without topic button */}
          <button
            className="phil-start-button"
            onClick={handleStartWithoutTopic}
            disabled={isLoading}
          >
            Begin a conversation
          </button>

          <p className="phil-start-hint">
            or select a topic from the sidebar
          </p>
        </div>
      </div>

      {/* Selected cluster indicator */}
      {selectedCluster && (
        <div className="phil-selected-topic">
          <span className="phil-selected-label">Ready to explore:</span>
          <span className="phil-selected-name">{selectedCluster.hubTag}</span>
        </div>
      )}
    </div>
  );

  const renderClusterList = () => {
    if (isLoading) {
      return (
        <div className="phil-clusters-loading">
          <div className="phil-loading-spinner" />
          <span>Finding your thinking patterns...</span>
        </div>
      );
    }

    if (error && !sessionActive) {
      return (
        <div className="phil-clusters-error">
          <p>{error}</p>
          <button className="phil-retry-button" onClick={handleRetry}>
            Try Again
          </button>
        </div>
      );
    }

    if (readyClusters.length === 0) {
      return (
        <div className="phil-clusters-empty">
          <p>No clusters are ready for philosophical exploration yet.</p>
          <p className="phil-clusters-hint">
            Continue building your knowledge base. Phil becomes available
            when you have enough connected thoughts on a topic.
          </p>
        </div>
      );
    }

    return (
      <div className="phil-clusters-list">
        <div className="phil-clusters-header">
          <h3>Topics Ready for Exploration</h3>
          <span className="phil-clusters-count">
            {totalReadyClusters} cluster{totalReadyClusters !== 1 ? 's' : ''} ready
          </span>
        </div>

        <div className="phil-clusters-items">
          {readyClusters.map((cluster, index) => (
            <button
              key={cluster.hubTag || index}
              className={`phil-cluster-item ${
                selectedCluster?.hubTag === cluster.hubTag ? 'selected' : ''
              }`}
              onClick={() => handleClusterSelect(cluster)}
              disabled={sessionActive}
            >
              <span className="phil-cluster-name">{cluster.hubTag}</span>
              <span className="phil-cluster-meta">
                {cluster.thoughtCount || cluster.entryCount} thought{(cluster.thoughtCount || cluster.entryCount) !== 1 ? 's' : ''}
              </span>
            </button>
          ))}
        </div>

        {totalReadyClusters > readyClusters.length && (
          <div className="phil-clusters-more">
            <span>+ {totalReadyClusters - readyClusters.length} more available</span>
          </div>
        )}
      </div>
    );
  };

  // =========================================
  // Component Render
  // =========================================

  return (
    <div className="phil-page">
      {/* Main area - conversation or visualization */}
      <div className={`phil-main ${sessionActive ? 'session-active' : ''}`}>
        {sessionActive ? renderConversation() : renderVisualizationPlaceholder()}
      </div>

      {/* Cluster selection sidebar - INTENTIONALLY HIDDEN during Phil 2.0 transition */}
      {/* This sidebar shows tag-based topic clusters. It's being replaced by synthesis-based conversation starters. */}
      {/* The code is preserved here and may be revived in a future redesign. */}
      {SHOW_TOPICS_SIDEBAR && (
        <aside className={`phil-sidebar ${sessionActive ? 'phil-sidebar-dimmed' : ''}`}>
          {renderClusterList()}

          {/* Session status when active */}
          {sessionActive && (
            <div className="phil-session-status">
              <div className="phil-session-indicator" />
              <span>Session active</span>
            </div>
          )}

          <div className="phil-sidebar-footer">
            <p className="phil-footer-note">
              Phil helps you examine your thinking—not as a therapist or guide,
              but as a mirror that surfaces patterns in your documented reflections.
            </p>
          </div>
        </aside>
      )}

      {/* Consent modal - shown on session end */}
      <PhilConsentModal
        isOpen={showConsentModal}
        onKeep={handleConsentKeep}
        onDiscard={handleConsentDiscard}
        isLoading={consentLoading}
      />

      {/* Orientation modal - shown on first visit or when reopened from sidebar */}
      <PhilOrientationModal
        isOpen={showOrientationModal}
        onComplete={orientationReadOnly ? handleCloseOrientationReadOnly : handleCompleteOrientation}
        readOnlyMode={orientationReadOnly}
      />
    </div>
  );
};

export default PhilPage;
